import { useEffect, useState } from "react";
import { useFormik } from "formik";
import InputField from "shared/InputField";
import { NavLink, useNavigate } from "react-router-dom";
import { countryList, nigerianStates } from "utilities/data";
import InputSelect from "shared/InputSelect";
import CompanyLogo from "shared/CompanyLogo";
import PasswordLength from "shared/PasswordLength";
import Signupframe from "assets/images/jpgs/signupframe.jpg";
import { TKIconEye, TKIconSpinner, TKIconViewEye } from "assets/images/svgs";
import { signUpValidationSchema } from "validation/signup/SignupSchema";
import { useDispatch, useSelector } from "react-redux";
import {
  createNewDonor,
  getToken,
  getCurrentDonor,
} from "appState/auth/authSlice";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

const DonorSignup = () => {
  const dispatch = useDispatch();
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showStates, setShowStates] = useState(false);
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");

  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    isAuthenticated && navigate("/");
  }, [isAuthenticated, navigate]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const calculateStrength = (value) => {
    let strength = 0;
    if (/\d/.test(value)) {
      strength++;
    }
    if (/[!@#$%^&*]/.test(value)) {
      strength++;
    }
    if (/[A-Z]/.test(value)) {
      strength++;
    }
    if (value.length >= 8) {
      strength++;
    }
    return strength;
  };

  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      phone: "",
      country: country,
      password: "",
    },
    validationSchema: signUpValidationSchema,
    onSubmit: (values) => {
      const body = {
        fullName: values.fullName,
        email: values.email,
        phone: phone,
        country: country,
        password: values.password,
      };
      dispatch(createNewDonor(body)).then((data) => {
        const { payload } = data;
        if (payload?.status === 200) {
          dispatch(getToken(payload));
          toast.success("Donor account created successfully");
          dispatch(getCurrentDonor()).then(() => {
            let path = "/donor";
            navigate(path);
          });
        }
      });
    },
  });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div>
      <div className="w-full h-full md:h-screen max-[950px]:flex-wrap flex justify-center items-center gap-8">
        <div className="relative w-[60%] max-[950px]:w-full max-md:h-[500px] h-screen">
          <img
            className="w-full h-full object-cover"
            src={Signupframe}
            alt="TK-SignupFrame"
          />
          <p className="px-8 absolute bottom-10 max-md:text-[14px] text-[28px] text-white font-semibold shadow-md">
            Speak up for those who cannot speak for themselves, for the rights
            of all who are destitute. Speak up and judge fairly; defend the
            rights of the poor and needy.
          </p>
        </div>
        <div className="px-4 w-[40%] h-full overflow-y-scroll md:max-h-[90%] max-md:h-full max-md:w-full">
          <div className="mb-2">
            <CompanyLogo blue />
          </div>
          <div className="mb-2">
            <p className="text-[20px] font-bold mb-2">Create a Donor Account</p>
            <div className="flex flex-wrap items-center justify-start gap-2">
              <p className="text-[#7A7A7A]">Already have an account? </p>
              <NavLink to="/auth/donor/login">
                <p className="underline text-[#213874] font-medium hover:font-semibold">
                  Login
                </p>
              </NavLink>
            </div>
          </div>
          <form className="w-full" onSubmit={formik.handleSubmit}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[14px] font-normal">Full Name</label>
                <InputField
                  className="border border-[#E6E6E6] rounded-md"
                  type="text"
                  name="fullName"
                  id="fullName"
                  placeholder="First name & Last Name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.fullName}
                />
                {formik.touched.fullName && formik.errors.fullName ? (
                  <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                    {formik.errors.fullName}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[14px] font-normal">Email Address</label>
                <InputField
                  className="border border-[#E6E6E6] rounded-md"
                  type="text"
                  name="email"
                  placeholder="exampleforyou@info.com"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email ? (
                  <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                    {formik.errors.email}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[14px] font-normal">Country</label>
                <InputSelect
                  className="h-[50px]"
                  options={countryList}
                  name="country"
                  optionsHolder="Select country"
                  onChange={(e) => {
                    if (e.target.value === "Nigeria") {
                      setShowStates(true);
                    } else {
                      setShowStates(false);
                    }
                    setCountry(e.target.value);
                    formik.handleChange(e);
                  }}
                  value={country}
                />
                {formik.touched.country && formik.errors.country ? (
                  <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                    {formik.errors.country}
                  </p>
                ) : null}
              </div>
              {showStates && (
                <div className="flex flex-col gap-2">
                  <label className="text-[14px] font-normal">State</label>
                  <InputSelect
                    className="h-[50px]"
                    options={nigerianStates}
                    name="state"
                    optionsHolder="Select state"
                    // onChange={formik.handleChange}=
                    value={formik.values.state}
                  />
                  {formik.touched.state && formik.errors.state ? (
                    <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                      {formik.errors.state}
                    </p>
                  ) : null}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="text-[14px] font-normal">Phone Number</label>
                <PhoneInput
                  placeholder="+2349012345678"
                  country={"ng"}
                  onlyCountries={["ng", "gh", "ke"]}
                  containerClass="donorSignupPhoneInput"
                  value={phone}
                  onChange={setPhone}
                  onBlur={formik.handleBlur}
                  inputProps={{
                    name: "phone",
                    required: true,
                    autoFocus: true,
                  }}
                />
                {formik.touched.phone && formik.errors.phone ? (
                  <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                    {formik.errors.phone}
                  </p>
                ) : null}
              </div>
              <div className="relative flex flex-col gap-2">
                <label className="text-[14px] font-normal">Password</label>
                <InputField
                  className="border border-[#E6E6E6] rounded-md"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="************"
                  onFocus={() => setIsFocused(true)}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                />
                {formik.touched.password && formik.errors.password ? (
                  <p className="text-red-500 text-xs mt-[8px] w-[300px] md:w-[424px]">
                    {formik.errors.password}
                  </p>
                ) : null}
                {showPassword ? (
                  <TKIconViewEye
                    onClick={toggleShowPassword}
                    className="absolute w-4 right-4 top-[43px] z-2 hover:cursor-pointer"
                  />
                ) : (
                  <TKIconEye
                    onClick={toggleShowPassword}
                    className="absolute w-4 right-4 top-[43px] z-2 hover:cursor-pointer"
                  />
                )}
                <div className="flex justify-between mt-[10px]">
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) === 1
                        ? "bg-red-500"
                        : calculateStrength(formik.values.password) === 2
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) === 3
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  />
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) === 2
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) === 3
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : ""
                    }`}
                  />
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) === 3
                        ? "bg-yellow-500"
                        : calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : ""
                    }`}
                  />
                  <PasswordLength
                    className={`strength-bar ${
                      isFocused ? "block" : "hidden"
                    } ${
                      calculateStrength(formik.values.password) >= 4
                        ? "bg-green-500"
                        : ""
                    }`}
                  />
                </div>
              </div>
              <div className="mb-6">
                <button
                  className="w-full h-[50px] flex justify-center items-center rounded-lg bg-[#213874] hover:bg-[#182d62] px-4 py-2"
                  type="submit"
                >
                  <p className="text-white font-semibold">
                    {isLoading ? <TKIconSpinner /> : "Create Account"}
                  </p>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DonorSignup;
